import { useEffect, useState, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

interface MetricCell {
  readonly value: number;
  readonly target: number | null;
  readonly isBreach: boolean;
  readonly metricDate: string;
}
interface AnalyticsDashboard {
  readonly metrics: Record<string, MetricCell>;
  readonly breachCount: number;
  readonly metricCount: number;
}

const METRIC_LABELS: Record<string, string> = {
  DEPLOYMENT_FREQUENCY: 'Deployment Frequency',
  LEAD_TIME: 'Lead Time (hrs)',
  CHANGE_FAILURE_RATE: 'Change Failure Rate %',
  ROLLBACK_RATE: 'Rollback Rate %',
  BUILD_SUCCESS_RATE: 'Build Success Rate %',
  DEPLOYMENT_SUCCESS_RATE: 'Deployment Success Rate %',
  RELEASE_VELOCITY: 'Release Velocity',
  MTTR: 'MTTR (min)',
};

/**
 * M32 — DevOps Analytics Dashboard (§31 / §23). The four DORA metrics plus the
 * ABWMS delivery KPIs (deployment frequency, lead time, change-failure &
 * rollback rate, build/deployment success, release velocity, MTTR) rendered as
 * the latest global value per metric, each flagged green/red against its
 * configured target (LAW 6 — targets from the Global Parameter Master).
 */
export function DevopsAnalyticsPage(): ReactNode {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [data, setData] = useState<AnalyticsDashboard | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    apiRequest<{ data: AnalyticsDashboard }>('/v1/devops/analytics/dashboard', { token })
      .then((r) => { if (alive) { setData(r.data); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token]);

  const codes = data ? Object.keys(data.metrics) : [];

  return (
    <main aria-label="devops-analytics">
      <h1>{t('m32.analytics.title')}</h1>
      {error && <p role="alert">{error}</p>}
      {loading || !data ? (
        <p>{t('m32.analytics.loading')}</p>
      ) : (
        <>
          <section aria-label="summary" className="summary">
            <p aria-label="metric-count">{t('m32.analytics.metricCount', { count: data.metricCount })}</p>
            <p aria-label="breach-count">{t('m32.analytics.breachCount', { count: data.breachCount })}</p>
          </section>
          {codes.length === 0 ? (
            <p aria-label="empty-metrics">{t('m32.analytics.empty')}</p>
          ) : (
            <section aria-label="kpi-cards" className="card-grid">
              {codes.map((code) => {
                const m = data.metrics[code];
                return (
                  <article key={code} aria-label={`card-${code}`} data-breach={m.isBreach ? 'yes' : 'no'}>
                    <h2>{METRIC_LABELS[code] ?? code}</h2>
                    <p aria-label={`value-${code}`}>{m.value}</p>
                    <p aria-label={`target-${code}`}>{m.target === null ? t('m32.analytics.noTarget') : t('m32.analytics.target', { value: m.target })}</p>
                    <p aria-label={`status-${code}`}>{m.isBreach ? 'BREACH' : 'OK'}</p>
                  </article>
                );
              })}
            </section>
          )}
        </>
      )}
    </main>
  );
}
